/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewTestCampaignLogs.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  IconButton,
  Stack,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { HandleApiActions } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { convertUtcToTimeZone } from "../../../utils/convertUtcToTimeZone";
import ViewFilesDialog from "./ViewFilesDialog";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

export default function ViewTestCampaignLogs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const profileDetails = useSelector((store) => store.profileReducer);

  const [selectedCampaignRunId, setSelectedCampaignRunId] = useState(null);
  const [campaignRunTestCasesList, setCampaignRunTestCasesList] = useState([]);

  const [tableRowsDataRun, setTableRowsDataRun] = useState([]);
  const [pageRun, setPageRun] = useState(1);
  const [rowsPerPageRun, setRowsPerPageRun] = useState(25);

  const [campaignResultTestCasesList, setCampaignResultTestCasesList] =
    useState([]);
  const [campaignResultTestCasesSummary, setCampaignResultTestCasesSummary] =
    useState(null);

  const [tableRowsDataResult, setTableRowsDataResult] = useState([]);
  const [pageResult, setPageResult] = useState(1);
  const [rowsPerPageResult, setRowsPerPageResult] = useState(25);

  const [openViewFilesDialog, setOpenViewFilesDialog] = useState(false);
  const [open, setOpen] = useState(false);

  const [selectedTestCaseData, setSelectedTestCaseData] = useState(null);

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const handleGetCampaignRunTestCases = async ({ campaignId }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_run_test_cases,
          params: {
            campaign_id: campaignId,
            page_number: pageRun,
            page_size: rowsPerPageRun,
          },
        }),
      );
      if (resp.error) {
        setCampaignRunTestCasesList([]);
      } else {
        setCampaignRunTestCasesList(resp?.data);
      }
    };

    if (id) {
      handleGetCampaignRunTestCases({ campaignId: id });
    }
  }, [id, pageRun, rowsPerPageRun, dispatch]);

  useEffect(() => {
    const handleGetCampaignResultTestCases = async ({ runId }) => {
      const resp = await dispatch(
        HandleApiActions({
          ...testCasesActions.get_campaign_result_test_cases,
          params: {
            run_id: runId,
            page_number: pageResult,
            page_size: rowsPerPageResult,
          },
        }),
      );
      if (resp.error) {
        setCampaignResultTestCasesList([]);
        setCampaignResultTestCasesSummary(null);
      } else {
        setCampaignResultTestCasesList(resp?.data?.results);
        setCampaignResultTestCasesSummary(resp?.data?.summary);
      }
    };

    if (selectedCampaignRunId) {
      handleGetCampaignResultTestCases({ runId: selectedCampaignRunId });
    }
  }, [selectedCampaignRunId, pageResult, rowsPerPageResult, dispatch]);

  useEffect(() => {
    const filteredCampaignRunTestCasesList = campaignRunTestCasesList?.map(
      (tc, index) => {
        return {
          ...tc,
          id: (pageRun - 1) * rowsPerPageRun + index + 1,
          executed_at: convertUtcToTimeZone(
            tc?.executed_at,
            profileDetails?.profile?.profile?.region
          ),
        };
      }
    );
    setTableRowsDataRun(filteredCampaignRunTestCasesList);
    setSelectedCampaignRunId(campaignRunTestCasesList?.[0]?.run_id);
  }, [
    campaignRunTestCasesList,
    pageRun,
    rowsPerPageRun,
    profileDetails?.profile?.profile?.region,
  ]);

  useEffect(() => {
    const filteredCampaignResultTestCasesList = campaignResultTestCasesList?.map(
      (tc, index) => {
        return {
          ...tc,
          id: (pageResult - 1) * rowsPerPageResult + index + 1,
        };
      }
    );
    setTableRowsDataResult(filteredCampaignResultTestCasesList);
  }, [campaignResultTestCasesList, pageResult, rowsPerPageResult]);

  const COLUMNS_RUN = [
    {
      field: "id",
      headerName: "S.No",
      flex: 0.4,
      minWidth: 60,
      maxWidth: 80,
      filterable: false,
      sortable: false,
    },
    { field: "run_id", headerName: "Run Id", flex: 1, minWidth: 80 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const statusMap = {
          "in-progress": "Inprogress",
          success: "Success",
          failed: "Failed",
        };
        return (
          <Typography sx={{ fontSize: "12px" }}>
            {statusMap[params.value] || params.value}
          </Typography>
        );
      },
    },
    {
      field: "executed_by_name",
      headerName: "Triggered By",
      flex: 1,
      minWidth: 120,
    },
    { field: "executed_at", headerName: "Run Date", flex: 1, minWidth: 120 },
    {
      field: "Select",
      headerName: "Edit",
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status = params?.row?.status;
        const isInProgress = status === "in-progress";
        const isCompleted = status === "success" || status === "failed";

        return (
          <Box>
            {isCompleted && (
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedCampaignRunId(params?.row?.run_id);
                  setOpen(true);
                }}
                sx={{ color: "#000" }}
                title="View Details"
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
            {isInProgress && (
              <IconButton
                size="small"
                onClick={() => {
                  navigate(
                    `/dashboard/test-cases/campaign/view/${id}?tab=run&run_id=${params?.row?.run_id}`
                  );
                }}
                sx={{ color: "#000" }}
                title="Edit"
              >
                <BorderColorIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];
  const COLUMNS_RESULT = [
    {
      field: "manual_test_case_id",
      headerName: "Test Case Id",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "test_case_name",
      headerName: "Test Case Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "result",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "artefact_link",
      headerName: "Uploaded Doc",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Button
          variant="text"
          size="small"
          disabled={!params?.row?.artefact_link?.length}
          sx={{
            ml: -1,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}
          onClick={() => {
            setSelectedTestCaseData({ ...params?.row });
            setOpenViewFilesDialog(true);
          }}
        >
          {"View"}
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 2, height: "100%" }}>
      

      
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        
        
        
        <CustomDataGridV2
          rows={tableRowsDataRun}
          columns={COLUMNS_RUN}
          isMultiLine={true}
          page={pageRun}
          rowsPerPage={rowsPerPageRun}
          handlePageChange={(newPage) => setPageRun(newPage)}
          handlePageSizeChange={(newSize) => {
            setRowsPerPageRun(newSize);
            setPageRun(1);
          }}
        />
        
        
      </Box>

      <CustomDialogBoxV2 open={open} onClose={handleClose}>
        <Stack spacing={2}>
          
          <Paper
            elevation={2}
            sx={{
              borderRadius: 0,
              pb: 2,
              width: "100%",
              mt: 2,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-start", p: 1 }}>
              
              <Typography>Run Details</Typography>
            </Box>

            
            <Divider sx={{ mb: 2, borderColor: "#bdbdbd" }} />

            
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "nowrap",
                overflowX: "auto",
                px: 2,
              }}
            >
              {[
                { label: "Run ID", value: selectedCampaignRunId || "-" },
                { label: "Trigger Type", value: "Manual" },
                {
                  label: "Triggered By",
                  value:
                    campaignResultTestCasesSummary?.executed_by_name || "-",
                },
                {
                  label: "Failed at",
                  value:
                    campaignResultTestCasesSummary?.failed_test_cases?.join(
                      ", "
                    ) || "-",
                },
              ].map(({ label, value }, index) => (
                <Box
                  key={label}
                  sx={{
                    flex: "1 1 auto",
                    minWidth: "150px",
                    minHeight: 56,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "13px" }}>{label}</Typography>
                  <Typography sx={{ fontSize: "12px" }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              mt: 3,
            }}
          >
            
            
            
            
            
            <CustomDataGridV2
              rows={tableRowsDataResult}
              columns={COLUMNS_RESULT}
              isMultiLine={true}
              page={pageResult}
              rowsPerPage={rowsPerPageResult}
              handlePageChange={(newPage) => setPageResult(newPage)}
              handlePageSizeChange={(newSize) => {
                setRowsPerPageResult(newSize);
                setPageResult(1);
              }}
            />
            
            
          </Box>
        </Stack>
      </CustomDialogBoxV2>

      
      <ViewFilesDialog
        open={openViewFilesDialog}
        onClose={() => setOpenViewFilesDialog(false)}
        dialogTitle="View Uploaded Files"
        selectedTestCaseData={selectedTestCaseData}
        setSelectedTestCaseData={setSelectedTestCaseData}
      />
    </Box>
  );
}

